import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { SkuCatalogEntry, SkuCatalogState } from "./use_sku_catalog";
import type { ApprovedPoSummary } from "./get_matrix";
import { TBD_PO_ID } from "./default_config";

/** The PO an added line lands on. Supplier + ship-to + both dates is the grain
 * the PDF groups on, so this identifies exactly one purchase order. */
export type AddSkuTarget = {
  /** The order the line joins. A placed order's own number, or the "TBD"
   * sentinel when the line opens an order that does not exist yet. */
  purchaseOrderId: string;
  supplier: string;
  location: string;
  orderPlacedDate: string;
  orderDeliveryDate: string;
};

/** The SKU and the numbers. The lane and the dates live on AddSkuTarget — a line
 * is described by what is being bought; the target says which order it goes on. */
export type AddSkuLine = {
  sku: string;
  skuName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
};

/** One selectable set of dates for a lane. Sourced from the purchasing calendar
 * AND from the POs already approved — a planner can edit a PO's dates before
 * approving it, and an edited date exists nowhere in the calendar. Offering only
 * calendar dates made adding to such a PO impossible: whatever you picked
 * carried a different ETA, and the ETA is what the PO number and the PDF group
 * on, so the line always arrived as a second PO. */
type Cycle = {
  key: string;
  orderDate: string;
  deliveryDate: string;
  /** Set when these dates belong to a PO that already exists. */
  po: ApprovedPoSummary | null;
};

const formatDate = (value: string): string => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const formatCurrency = (n: number): string =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** The catalog carries full ISO timestamps; a PO summary carries day tokens. */
const dayOf = (iso: string): string => (iso ? iso.slice(0, 10) : "");

/** Day token back to the midnight-UTC ISO the save writes. The plan truncates
 * both dates to the day, so this reproduces the stored value exactly — and
 * therefore groups with the PO's existing lines rather than beside them. */
const isoOfDay = (day: string): string => (day ? `${day}T00:00:00.000Z` : "");

/** Trim + lowercase. Used on SKU codes AND on supplier/location, because every
 * comparison below crosses two datasets — the calendar transform and
 * approved_pos — and a case or whitespace difference would silently drop the
 * match. assign_po_numbers.ts laneToken normalises lanes for the same reason. */
const norm = (value: string): string => value.trim().toLowerCase();

/** A cycle a planner can still open keys on the delivery day, which is what
 * identifies a PO that has no number yet. Not the order day — assign_po_numbers.ts
 * poGroupKey ignores that, so two options differing only there would be one PO
 * wearing two labels. */
const cycleKey = (deliveryDay: string): string => deliveryDay;

/** An order already placed is offered under its own number: one lane can carry
 * two of them due the same day, and the delivery day alone would hide one. */
const poCycleKey = (po: ApprovedPoSummary): string =>
  po.purchaseOrderId && po.purchaseOrderId !== TBD_PO_ID
    ? po.purchaseOrderId
    : cycleKey(po.orderDeliveryDay);

const cycleLabel = (c: Cycle): string => {
  const dates = `Order ${formatDate(c.orderDate)} · ETA ${formatDate(c.deliveryDate)}`;
  if (!c.po) return `${dates} · new PO`;
  const name = c.po.poNumber || c.po.purchaseOrderId || "existing PO";
  return `${dates} · adds to ${name} (${c.po.lineCount} ${
    c.po.lineCount === 1 ? "line" : "lines"
  })`;
};

export const AddSkuDialog = ({
  open,
  onClose,
  onConfirm,
  catalog,
  approvedPos,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (line: AddSkuLine, target: AddSkuTarget) => void;
  catalog: SkuCatalogState;
  /** The POs on the Approved tab — the only place a planner-edited date lives. */
  approvedPos: ApprovedPoSummary[];
}) => {
  const theme = useTheme();
  const [supplier, setSupplier] = React.useState<string | null>(null);
  const [location, setLocation] = React.useState<string | null>(null);
  const [cycleId, setCycleId] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<SkuCatalogEntry | null>(null);
  const [quantity, setQuantity] = React.useState("");
  const [unitCost, setUnitCost] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setSupplier(null);
      setLocation(null);
      setCycleId(null);
      setSelected(null);
      setQuantity("");
      setUnitCost("");
    }
  }, [open]);

  const suppliers = React.useMemo(
    () => [...new Set(catalog.entries.map((e) => e.supplier))].sort(),
    [catalog.entries],
  );

  // Only the locations this supplier actually ships to, so an invalid pair
  // cannot be picked. Same idea for the SKU list below.
  const locations = React.useMemo(
    () =>
      supplier
        ? [
            ...new Set(
              catalog.entries
                .filter((e) => e.supplier === supplier)
                .map((e) => e.location),
            ),
          ].sort()
        : [],
    [catalog.entries, supplier],
  );

  const cycles = React.useMemo<Cycle[]>(() => {
    if (!supplier || !location) return [];
    const byKey = new Map<string, Cycle>();
    for (const e of catalog.entries) {
      if (norm(e.supplier) !== norm(supplier)) continue;
      if (norm(e.location) !== norm(location)) continue;
      // cellToDate yields "" for a null date; neither end can be ordered into.
      if (!e.orderDate || !e.deliveryDate) continue;
      const k = cycleKey(dayOf(e.deliveryDate));
      if (!byKey.has(k)) {
        byKey.set(k, {
          key: k,
          orderDate: e.orderDate,
          deliveryDate: e.deliveryDate,
          po: null,
        });
      }
    }
    for (const po of approvedPos) {
      if (norm(po.supplier) !== norm(supplier)) continue;
      if (norm(po.location) !== norm(location)) continue;
      // A PO with no ETA has no identity to join — poGroupKey would fold it in
      // with every other ETA-less row on the lane.
      if (!po.orderDeliveryDay) continue;
      // The calendar's open cycle for this ETA is this very purchase order, so
      // it goes: the line joins this PO whichever option the planner clicks, and
      // only the PO's own order date keeps the row key on the PO's existing
      // lines, where the duplicate-SKU guard can see them. Another PO's option
      // stays — the lane can hold more than one.
      const etaKey = cycleKey(po.orderDeliveryDay);
      const openCycle = byKey.get(etaKey);
      if (openCycle && !openCycle.po) byKey.delete(etaKey);
      const k = poCycleKey(po);
      byKey.set(k, {
        key: k,
        orderDate: isoOfDay(po.orderPlacedDay),
        deliveryDate: isoOfDay(po.orderDeliveryDay),
        po,
      });
    }
    return [...byKey.values()].sort(
      (a, b) =>
        a.orderDate.localeCompare(b.orderDate) ||
        a.deliveryDate.localeCompare(b.deliveryDate),
    );
  }, [catalog.entries, approvedPos, supplier, location]);

  // Deliberately NOT defaulted. The old dialog pre-filled the nearest week, so
  // a planner who never looked at this field had silently accepted it — and if
  // their PO was in the other week, the line opened a second PO.
  const cycle = React.useMemo(
    () => cycles.find((c) => c.key === cycleId) ?? null,
    [cycles, cycleId],
  );

  // Drop a stale selection if a refreshed catalog no longer offers it. The
  // subscription emits more than once (cached, then fresh).
  React.useEffect(() => {
    setCycleId((current) =>
      current && cycles.some((c) => c.key === current) ? current : null,
    );
  }, [cycles]);

  // Every SKU this vendor ships to this location, deduped. NOT filtered by the
  // chosen dates: an edited PO date has no calendar row behind it, so filtering
  // on it would empty the list for exactly the POs this dialog exists to serve.
  // The catalog entry only supplies name/cost/MOQ; the dates come from `cycle`.
  const laneEntries = React.useMemo(() => {
    if (!supplier || !location) return [];
    // Distance from a catalog row's delivery day to the chosen cycle's. 0 is an
    // exact cycle match; Infinity when no cycle is chosen yet (first row wins,
    // as before) and for a row whose delivery day is missing or unparseable.
    // cellToDate yields "" for a null date and the raw string for anything it
    // cannot parse, and Date.parse of either is NaN — every comparison against
    // NaN is false, so such a row would be held forever and shadow the real one.
    const cycleDay = cycle ? Date.parse(dayOf(cycle.deliveryDate)) : NaN;
    const cycleDistance = (e: SkuCatalogEntry): number => {
      if (Number.isNaN(cycleDay)) return Number.POSITIVE_INFINITY;
      const day = Date.parse(dayOf(e.deliveryDate));
      return Number.isNaN(day)
        ? Number.POSITIVE_INFINITY
        : Math.abs(day - cycleDay);
    };
    const bySku = new Map<string, SkuCatalogEntry>();
    for (const e of catalog.entries) {
      if (norm(e.supplier) !== norm(supplier)) continue;
      if (norm(e.location) !== norm(location)) continue;
      // Prefer the row from the chosen cycle, so cost and MOQ come from the
      // cycle being ordered into when the calendar does carry it. Matched on
      // the delivery day, the same thing the cycle keys on — matching the order
      // day instead would pick the wrong row whenever a PO's order date was
      // edited away from the calendar's.
      //
      // An edited PO date matches no calendar row at all, so nothing is
      // "preferred" and the winner would otherwise be whichever row iterated
      // first — an arbitrary cost prefilled for exactly the POs this dialog
      // serves. Fall back to the nearest delivery day instead, so the cost and
      // MOQ come from the closest real cycle and the choice is reproducible.
      const held = bySku.get(e.sku);
      if (!held) {
        bySku.set(e.sku, e);
        continue;
      }
      // Equidistant — an ETA edited to exactly midway between two cycles —
      // falls back to the earlier delivery day, so the prefilled cost cannot
      // depend on parquet row order.
      const dNew = cycleDistance(e);
      const dHeld = cycleDistance(held);
      if (
        dNew < dHeld ||
        (dNew === dHeld && dayOf(e.deliveryDate) < dayOf(held.deliveryDate))
      ) {
        bySku.set(e.sku, e);
      }
    }
    return [...bySku.values()].sort((a, b) => a.sku.localeCompare(b.sku));
  }, [catalog.entries, supplier, location, cycle]);

  // Normalised on both sides. The two sides come from different datasets — the
  // catalog transform and approved_pos — so "ABC-123" against "abc-123" would
  // offer a SKU already on the PO, and the resolver's own duplicate backstop
  // compares verbatim too, so nothing else would catch it.
  const alreadyOnPo = React.useMemo(
    () => new Set((cycle?.po?.skus ?? []).map(norm)),
    [cycle],
  );

  // Scope back to the chosen cycle whenever the calendar carries rows for that
  // day. The calendar deciding which SKUs are orderable in a week is a live
  // constraint (lead time, vendor cadence) and nothing downstream re-checks it,
  // since handleConfirm takes its dates from `cycle`, not from the selection.
  //
  // Keyed on "has calendar rows", NOT on whether a PO is attached: `cycles`
  // above deliberately lets an approved PO REPLACE the calendar cycle sharing
  // its ETA, so an unedited PO — the ordinary case — has a PO and real calendar
  // rows both. Gating on the PO would leave the constraint dropped for exactly
  // those. Only a day the calendar has no rows for, i.e. an edited ETA, keeps
  // the wider lane list, and there it is the whole point: filtering would empty
  // the picker for precisely the POs this dialog exists to serve.
  const skus = React.useMemo(() => {
    const cycleDay = cycle ? dayOf(cycle.deliveryDate) : "";
    const onCycleDay = (e: SkuCatalogEntry) =>
      dayOf(e.deliveryDate) === cycleDay;
    // laneEntries holds the nearest row per SKU, so a SKU the calendar offers
    // on this day is held at distance 0 — `some` is a faithful test for "the
    // calendar has rows here".
    const calendarBacked = !!cycle && laneEntries.some(onCycleDay);
    const offered = calendarBacked
      ? laneEntries.filter(onCycleDay)
      : laneEntries;
    return offered.filter((e) => !alreadyOnPo.has(norm(e.sku)));
  }, [laneEntries, alreadyOnPo, cycle]);

  React.useEffect(() => {
    setSelected((current) => {
      if (!current) return current;
      if (skus.some((e) => e.sku === current.sku)) return current;
      setUnitCost("");
      return null;
    });
  }, [skus]);

  const handleSelect = (entry: SkuCatalogEntry | null) => {
    setSelected(entry);
    // Prefill from the master so the common case is type-a-quantity-and-go.
    setUnitCost(
      entry?.cost !== null && entry?.cost !== undefined
        ? String(entry.cost)
        : "",
    );
  };

  const parsedQuantity = Number(quantity.replace(/,/g, ""));
  const parsedUnitCost = Number(unitCost.replace(/,/g, ""));
  // Whole units only: quantity is eaches on the PO line and reaches the vendor
  // document as typed, so "1.5" would print a fractional case count and round
  // differently in the export than on screen.
  const quantityValid =
    quantity !== "" && Number.isInteger(parsedQuantity) && parsedQuantity > 0;
  const unitCostValid =
    unitCost !== "" && Number.isFinite(parsedUnitCost) && parsedUnitCost >= 0;
  const canConfirm = !!cycle && !!selected && quantityValid && unitCostValid;

  const cases =
    selected?.qtyPerCase && selected.qtyPerCase > 0 && quantityValid
      ? parsedQuantity / selected.qtyPerCase
      : null;
  // A warning, not a block: a planner may knowingly order under the minimum.
  const belowMoq =
    selected?.moq != null && quantityValid && parsedQuantity < selected.moq;
  // Also a warning, not a block. Zero is usually a mistake on a purchase line,
  // but a planner may legitimately raise one for a free or sample shipment, and
  // the cost is informational on the PO — refusing it would be the wrong call.
  const zeroCost = unitCostValid && parsedUnitCost === 0;

  const handleConfirm = () => {
    if (!canConfirm || !selected || !cycle) return;
    onConfirm(
      {
        sku: selected.sku,
        skuName: selected.skuName,
        quantity: parsedQuantity,
        unitCost: parsedUnitCost,
        // Rounded like the grid's own quantity/cost interlock: this lands in
        // approved_pos and then the CSV and PDF, and 3 x 19.99 stores
        // 59.970000000000006 while the dialog above reads $59.97.
        totalCost: Math.round(parsedQuantity * parsedUnitCost * 100) / 100,
      },
      {
        // From the PO's own rows when the planner picked an existing PO, so an
        // edited date is carried verbatim and the line joins that PO.
        purchaseOrderId: cycle.po?.purchaseOrderId || TBD_PO_ID,
        supplier: cycle.po ? cycle.po.supplier : selected.supplier,
        location: cycle.po ? cycle.po.location : selected.location,
        orderPlacedDate: cycle.orderDate,
        orderDeliveryDate: cycle.deliveryDate,
      },
    );
  };

  // What the planner is about to write, spelled out. The failure this dialog
  // used to have was invisible at confirm time: the line looked added, and only
  // the PDF showed it had opened a PO of its own.
  const outcome = (): string | null => {
    if (!cycle || !selected) return null;
    if (cycle.po) {
      const joins = `This line joins ${cycle.po.poNumber || "the existing PO"} — ${
        cycle.po.supplier
      } · ${cycle.po.location} · ETA ${formatDate(cycle.deliveryDate)}.`;
      // An order goes to the vendor whole, so adding to one that has not been
      // signed off puts all of it on the send list. Said here because the tab
      // the planner is standing on is where the order then disappears from.
      if (!cycle.po.approved) {
        return `${joins} Its other ${cycle.po.lineCount} ${
          cycle.po.lineCount === 1 ? "line goes" : "lines go"
        } to the send list with it.`;
      }
      return joins;
    }
    return `This creates a NEW purchase order: ${selected.supplier} · ${
      selected.location
    } · Order ${formatDate(cycle.orderDate)} · ETA ${formatDate(
      cycle.deliveryDate,
    )}.`;
  };

  const skuHelperText = (): string => {
    if (!supplier || !location) return "Choose a supplier and location first";
    if (!cycle) return "Choose a PO date first";
    if (skus.length > 0) return `${skus.length} available`;
    if (laneEntries.length === 0) return "No SKUs are orderable on this lane";
    // Whatever was offered here was non-empty — a calendar-backed day always
    // has at least one row on it (that is what makes it backed), and an
    // unbacked day offers the whole lane, which the branch above just proved
    // non-empty. So the only thing that can have emptied `skus` is alreadyOnPo.
    return "Every orderable SKU is already on this PO";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>Add SKU to PO</Box>
        <IconButton
          onClick={onClose}
          sx={{ p: 0, color: theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {catalog.loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading SKUs…</Typography>
          </Box>
        )}

        {catalog.error && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <Typography variant="body2" color="error">
              {catalog.error}
            </Typography>
            <Button size="small" onClick={catalog.retry}>
              Retry
            </Button>
          </Box>
        )}

        {!catalog.loading && !catalog.error && (
          <>
            <Autocomplete
              options={suppliers}
              value={supplier}
              onChange={(_, v) => {
                setSupplier(v);
                setLocation(null);
                setCycleId(null);
                handleSelect(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Supplier"
                  variant="standard"
                  margin="normal"
                  helperText={`${suppliers.length} suppliers`}
                />
              )}
            />

            <Autocomplete
              options={locations}
              value={location}
              disabled={!supplier}
              onChange={(_, v) => {
                setLocation(v);
                setCycleId(null);
                handleSelect(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  variant="standard"
                  margin="normal"
                  helperText={
                    supplier
                      ? `${locations.length} ship-to locations for this supplier`
                      : "Choose a supplier first"
                  }
                />
              )}
            />

            {/* Always rendered, even for a single option: hiding it left the
                dates set by something the planner could not see. */}
            <Autocomplete
              options={cycles.map((c) => c.key)}
              value={cycleId}
              disabled={!supplier || !location}
              onChange={(_, v) => {
                setCycleId(v);
                handleSelect(null);
              }}
              getOptionLabel={(k) => {
                const c = cycles.find((x) => x.key === k);
                return c ? cycleLabel(c) : k;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="PO date"
                  variant="standard"
                  margin="normal"
                  helperText={
                    !supplier || !location
                      ? "Choose a supplier and location first"
                      : "Dates already on an approved PO are listed too"
                  }
                />
              )}
            />

            <Autocomplete
              options={skus}
              value={selected}
              disabled={!cycle}
              onChange={(_, v) => handleSelect(v)}
              getOptionLabel={(o) =>
                o.skuName ? `${o.sku} — ${o.skuName}` : o.sku
              }
              isOptionEqualToValue={(a, b) => a.sku === b.sku}
              noOptionsText="No SKUs left to add"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="SKU"
                  variant="standard"
                  margin="normal"
                  helperText={skuHelperText()}
                />
              )}
            />

            {selected && cycle && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {outcome()}
                  {selected.moq != null
                    ? ` MOQ ${selected.moq.toLocaleString("en-US")}.`
                    : ""}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <TextField
                label="Quantity (units)"
                variant="standard"
                margin="normal"
                fullWidth
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={quantity !== "" && !quantityValid}
                helperText={
                  quantity !== "" && !quantityValid
                    ? "Enter a whole quantity greater than zero"
                    : belowMoq
                      ? `Below the ${selected?.moq?.toLocaleString("en-US")} minimum`
                      : cases !== null
                        ? `${cases.toLocaleString("en-US", { maximumFractionDigits: 2 })} cases`
                        : " "
                }
              />
              <TextField
                label="Unit cost"
                variant="standard"
                margin="normal"
                fullWidth
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                error={unitCost !== "" && !unitCostValid}
                helperText={
                  unitCost !== "" && !unitCostValid
                    ? "Enter a valid cost"
                    : zeroCost
                      ? "Zero cost — this line will total $0"
                      : " "
                }
              />
            </Box>

            <Typography variant="body2" sx={{ mt: 1 }}>
              Total:{" "}
              <strong>
                {canConfirm
                  ? formatCurrency(parsedQuantity * parsedUnitCost)
                  : "—"}
              </strong>
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              Added lines are approved immediately and appear here, then flow to
              the PO PDF and the approved-POs export. Un-ticking one deletes it
              — unlike a suggested line, it has no Needs Review row to return
              to.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleConfirm}
          variant="contained"
          // The form hides while the catalog reloads, but this button lives
          // outside that guard — leaving it live would submit the stale
          // selection against the new run's cycle.
          disabled={!canConfirm || catalog.loading || !!catalog.error}
        >
          Add to PO
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
